<script lang="ts">
	import Link from "../Link.svelte";
	import { toTimeFormat } from "../../../../core/utils/toTimeFormat.js";
	import { toDateMonthFormat } from "../../../../core/utils/toDateMonthFormat.js";
	import Message from "../Message.svelte";
	import Tooltip from "../Tooltip.svelte";
	import type { ProductForCustomer } from "../../../../core/schema/Product";
	import { getGlobalSettings } from "../../getGlobalSettings";
	import Button from "../Button.svelte";
	import { getNearestDeliveryDateForProduct } from "../../../../core/schema/utils/getNearestDeliveryDate";
	import { CustomerType } from "../../../../core/schema/CustomerType";
	import { toDayLong } from "../../../../core/utils/toDayLong";
	import { isProductCurrentlyPurchasable } from "../../../../core/schema/utils/isProductPurchasable";

	export let product: ProductForCustomer;
	export let allowExpress: boolean;
	export let isExpress: boolean;

	function toggleExpressVariant(): void {
		const searchParams = new URLSearchParams(window.location.search);
		if (isExpress) {
			searchParams.delete("express");
		} else {
			searchParams.set("express", "");
		}
		isExpress = !isExpress;

		window.history.pushState({}, "", `${window.location.pathname}?${searchParams.toString()}`);
	}

	const { TENANT_LOCALE } = getGlobalSettings();

	const dayInflectMap: Record<string, string> = {
		neděle: "neděli",
		pondělí: "pondělí",
		úterý: "úterý",
		středa: "středu",
		čtvrtek: "čtvrtek",
		pátek: "pátek",
		sobota: "sobotu",
	};

	$: nearestDeliveryDate = getNearestDeliveryDateForProduct(product, CustomerType.B2c, false);
	$: nearestDeliveryDateExpress = getNearestDeliveryDateForProduct(product, CustomerType.B2c, true);
	$: dayLong = nearestDeliveryDate && toDayLong(nearestDeliveryDate, TENANT_LOCALE);
</script>

{#if nearestDeliveryDate && nearestDeliveryDateExpress && dayLong && isProductCurrentlyPurchasable(product, CustomerType.B2c, isExpress)}
	{#if isExpress}
		{#if !product.soldOut}
			<div class="flex flex-wrap items-center justify-between gap-2 pb-3 lg:flex-nowrap">
				<div>
					<p>
						<span class="font-semibold"> Potřebujete jinou velikost nebo úpravu dortu? </span>
					</p>
					<p class="text-xs">
						Podívejte se na náš standardní produkt, který umíme nejdřív k vyzvednutí {dayLong ===
							dayInflectMap["středa"] || dayLong === dayInflectMap["čtvrtek"]
							? "ve"
							: "v"}
						{dayInflectMap[dayLong]}
						{toDateMonthFormat(nearestDeliveryDate, TENANT_LOCALE)} od {toTimeFormat(
							nearestDeliveryDate,
							TENANT_LOCALE,
						)}.
					</p>
				</div>
				<Button
					class="h-min min-w-fit"
					variant="secondary"
					on:click={() => {
						toggleExpressVariant();
					}}
				>
					Zobrazit všechny úpravy
				</Button>
			</div>
		{/if}
		<Message variant="express" class="mt-3">
			<p class="!text-base font-semibold">K vyzvednutí už zítra</p>
			<p>
				Tento produkt si můžete vyzvednout už zítra od {toTimeFormat(nearestDeliveryDateExpress, TENANT_LOCALE)}
				na pobočce v Praze Čakovicích nebo Petřinách.
			</p>
		</Message>
	{:else}
		<Message variant="white">
			<Tooltip class="relative mb-2 w-fit cursor-pointer pr-4 text-base font-semibold text-black">
				K vyzvednutí nejdříve
				<span
					class="bg-grey-200 absolute right-0 top-0 ml-0.5 grid aspect-square h-3 place-items-center rounded-full text-[9px] font-bold"
				>
					?
				</span>

				<div slot="tooltip" class="max-w-96">
					Potřebujete dort na poslední chvíli a systém Vám neumožní objednat třeba na zítra?

					{#if allowExpress}
						<strong class="font-semibold">Klikněte na „Chci to už zítra“</strong>
						a podívejte se na naši expresní nabídku, nebo zavolejte
					{:else}
						Zavolejte prosím
					{/if}
					na jednu z našich poboček
					<Link class="font-semibold" href="tel:00420775033520">Čakovice</Link>
					nebo <Link class="font-semibold" href="tel:00420775033520">Petřiny</Link> a kolegové s Vámi zpracují „Last call“
					objednávku.
				</div>
			</Tooltip>
			<p class="text-xs">
				{dayLong === dayInflectMap["středa"] || dayLong === dayInflectMap["čtvrtek"] ? "Ve" : "V"}
				<span class="font-semibold">
					{dayInflectMap[dayLong]}
					{toDateMonthFormat(nearestDeliveryDate, TENANT_LOCALE)} od {toTimeFormat(nearestDeliveryDate, TENANT_LOCALE)}
				</span> na pobočce v Praze Čakovicích nebo Petřinách.
			</p>
		</Message>
		{#if allowExpress}
			<Message variant="express" class="mt-3" iconClass="h-6">
				<div class="flex items-center justify-between">
					<div>
						<p class="!text-base font-semibold">Potřebujete dort dříve?</p>
						<p>
							<span class="font-semibold"> Umíme to dříve </span> s naší <span class="font-semibold"> expresní </span>
							variantou.
						</p>
					</div>
					<Button
						class="h-min min-w-fit"
						variant="secondary"
						on:click={() => {
							toggleExpressVariant();
						}}
					>
						Chci to už zítra
					</Button>
				</div>
			</Message>
		{/if}
	{/if}
{:else}
	<Message variant="info">
		<p>
			Omlouváme se, ale produkt {product.name[TENANT_LOCALE]} je
			<span class="font-semibold">aktuálně nedostupný a nelze jej objednat</span>.
		</p>
	</Message>
{/if}
